import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    BrainCircuit, ArrowLeft, CheckCircle2, XCircle, Sparkles, ShieldAlert,
    FileText, Save, Loader2, User2, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/roles";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";

function confidenceClass(c) {
    if (c === null || c === undefined) return "badge-mock";
    if (c >= 0.85) return "badge-exito";
    if (c >= 0.7) return "badge-gold";
    return "badge-error";
}

export default function RevisionHIL() {
    const { id } = useParams();
    const nav = useNavigate();
    const { hasRole } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [edited, setEdited] = useState({});
    const [motivo, setMotivo] = useState("");
    const [aplicarPersona, setAplicarPersona] = useState(true);
    const [openReject, setOpenReject] = useState(false);
    const [busy, setBusy] = useState(false);

    const canResolve = hasRole([
        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.CUMPLIMIENTO, ROLES.ADMIN_TI,
        ROLES.DIRECCION, ROLES.AUDITOR,
    ]);

    const load = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get(`/hil-queue/${id}`);
            setData(d);
            const orig = {};
            Object.entries(d.extraction?.campos_extraidos || {}).forEach(([k, v]) => {
                orig[k] = v?.valor ?? "";
            });
            setEdited(orig);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No encontrado");
            nav("/ia/hil");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const asignarme = async () => {
        setBusy(true);
        try {
            const { data: me } = await api.get("/auth/me");
            await api.post(`/hil-queue/${id}/asignar`, { usuario_email: me.email });
            toast.success("Asignado a ti");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const resolver = async (resolucion) => {
        if (!motivo.trim()) return toast.error("Motivo requerido");
        setBusy(true);
        try {
            // Detectar cambios respecto a valores originales
            const originales = data.extraction?.campos_extraidos || {};
            const corregidos = {};
            Object.entries(edited).forEach(([k, v]) => {
                const orig = originales[k]?.valor;
                if (v !== orig && v !== null && v !== "") corregidos[k] = v;
            });

            const payload = {
                resolucion,
                motivo,
                campos_corregidos: Object.keys(corregidos).length > 0 ? corregidos : null,
                aplicar_a_persona: aplicarPersona,
            };
            const { data: res } = await api.post(`/hil-queue/${id}/resolver`, payload);
            toast.success(`Resuelto · ${resolucion.replace(/_/g, " ")}`);
            if (res.persona_id) toast.info(`Aplicado a persona ${res.persona_id.slice(0, 8)}…`);
            setTimeout(() => nav("/ia/hil?estado=pendiente"), 1200);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    if (loading) {
        return (
            <div className="p-10 flex items-center gap-3 text-muted-foreground" data-testid="revision-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
        );
    }
    if (!data) return null;

    const { item, extraction, documento } = data;
    const campos = extraction?.campos_extraidos || {};
    const originalKeys = Object.keys(campos);
    const isResuelto = item.estado === "resuelto";

    return (
        <div className="p-8 lg:p-10 max-w-[1700px]" data-testid="revision-hil-page">
            <div className="mb-4">
                <Link to="/ia/hil" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3 w-3" /> Cola HIL
                </Link>
            </div>
            <div className="flex items-start gap-4 flex-wrap mb-6">
                <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2 flex items-center gap-2">
                        <Sparkles className="h-3 w-3" /> Revisión humana · {item.tipo_revision.replace(/_/g, " ")}
                    </div>
                    <h1 className="font-display text-3xl flex items-center gap-3">
                        <BrainCircuit className="h-7 w-7 text-[color:var(--brand-dark-gold)]" />
                        Extracción IA · {extraction?.tipo_documento_detectado?.replace(/_/g, " ")}
                    </h1>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <span className={`badge-gold text-xs`}>Prioridad {item.prioridad}</span>
                        <span className="text-xs text-muted-foreground">
                            SLA {item.sla_horas}h · creado {item.fecha_creacion?.slice(0, 16)}
                        </span>
                        {item.asignado_a && (
                            <span className="text-xs flex items-center gap-1">
                                <User2 className="h-3 w-3" /> {item.asignado_a}
                            </span>
                        )}
                    </div>
                </div>
                {!isResuelto && !item.asignado_a && canResolve && (
                    <Button variant="outline" onClick={asignarme} disabled={busy} data-testid="hil-asignarme-btn">
                        Asignarme
                    </Button>
                )}
            </div>

            {item.motivo && (
                <div className="rounded-md border border-[color:var(--brand-coral)]/40 bg-[color:var(--brand-coral)]/5 p-3 mb-6 text-sm flex gap-2" data-testid="hil-motivo">
                    <AlertTriangle className="h-4 w-4 text-[color:var(--brand-coral)] shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-[color:var(--brand-coral)]">Motivo de revisión</div>
                        <div className="text-muted-foreground mt-0.5">{item.motivo}</div>
                        {item.matches_lista_negra?.length > 0 && (
                            <div className="mt-2 text-xs">
                                <span className="font-semibold">Matches lista negra:</span>
                                <ul className="list-disc ml-5">
                                    {item.matches_lista_negra.map((m, i) => (
                                        <li key={i}>{m.nombre} — {m.fuente} ({m.id})</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Doc preview */}
                <div className="rounded-md border border-border bg-card p-4">
                    <h3 className="font-display text-lg flex items-center gap-2 mb-3">
                        <FileText className="h-4 w-4 text-[color:var(--brand-dark-gold)]" /> Documento
                    </h3>
                    <div className="space-y-2 text-sm">
                        <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Nombre</span><br/>
                            <span className="font-medium">{documento?.nombre}</span>
                        </div>
                        <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Tipo taxonomía</span><br/>
                            <span className="badge-gold">{documento?.tipo_taxonomia}</span>
                        </div>
                        <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Filename</span><br/>
                            <span className="text-xs">{documento?.filename}</span>
                        </div>
                        <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Hash SHA-256</span><br/>
                            <span className="hash-cell text-xs">{documento?.hash?.slice(0, 32)}…</span>
                        </div>
                        <div className="pt-2">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documentos/${documento?.id}/download`}
                                target="_blank" rel="noopener noreferrer">
                                <Button size="sm" variant="outline" data-testid="download-doc-btn">
                                    Descargar / previsualizar
                                </Button>
                            </a>
                        </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-border">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Metadata LLM</div>
                        <div className="text-xs space-y-1">
                            <div>Modelo: <span className="font-medium">{extraction?.llm_model_usado}</span></div>
                            <div>Costo extracción: <span className="tabular">${extraction?.llm_costo_estimado_usd?.toFixed(4)}</span></div>
                            <div>Confianza clasificación:
                                <span className={`${confidenceClass(extraction?.confianza_clasificacion)} ml-1`}>
                                    {(extraction?.confianza_clasificacion * 100).toFixed(0)}%
                                </span>
                            </div>
                            <div className="hash-cell text-[10px]">prompt hash: {extraction?.llm_prompt_hash?.slice(0, 20)}…</div>
                        </div>
                    </div>
                </div>

                {/* Campos extraídos + editor */}
                <div className="rounded-md border border-border bg-card p-4">
                    <h3 className="font-display text-lg flex items-center gap-2 mb-3">
                        <Sparkles className="h-4 w-4 text-[color:var(--brand-dark-gold)]" /> Campos extraídos
                        {isResuelto && (
                            <span className="badge-exito ml-2 text-xs"><CheckCircle2 className="h-3 w-3" /> Resuelto</span>
                        )}
                    </h3>
                    <div className="space-y-3 max-h-[520px] overflow-y-auto">
                        {originalKeys.map((k) => {
                            const v = campos[k];
                            const isArray = Array.isArray(v?.valor);
                            const originalDisplay = isArray
                                ? JSON.stringify(v.valor).slice(0, 80) + "…"
                                : v?.valor;
                            return (
                                <div key={k} className="pb-3 border-b border-border/40" data-testid={`extraction-field-${k}`}>
                                    <div className="flex items-baseline justify-between gap-2 mb-1">
                                        <span className="text-xs font-medium uppercase tracking-wider">{k.replace(/_/g, " ")}</span>
                                        <span className={`${confidenceClass(v?.confianza)} text-[10px]`}
                                            data-testid={`confianza-badge-${k}`}>
                                            {v?.confianza ? `${(v.confianza * 100).toFixed(0)}%` : "—"}
                                            {v?.revisado_humano && " · ✓"}
                                        </span>
                                    </div>
                                    {!isResuelto && !isArray ? (
                                        <Input
                                            value={edited[k] ?? ""}
                                            onChange={(e) => setEdited({ ...edited, [k]: e.target.value })}
                                            className="text-sm"
                                            data-testid={`hil-corregir-${k}`}
                                        />
                                    ) : (
                                        <div className="text-sm mt-1 font-mono bg-muted/50 px-2 py-1 rounded-sm">
                                            {originalDisplay ?? <span className="text-muted-foreground italic">null</span>}
                                        </div>
                                    )}
                                    {!isResuelto && !isArray && edited[k] !== (v?.valor ?? "") && (
                                        <div className="text-[10px] text-[color:var(--brand-dark-gold)] mt-1">
                                            LLM: <span className="line-through">{v?.valor ?? "null"}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {originalKeys.length === 0 && (
                            <div className="text-sm text-muted-foreground italic">Sin campos extraídos.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Panel de resolución */}
            {!isResuelto && canResolve && (
                <div className="mt-6 rounded-md border border-border bg-card p-5" data-testid="resolucion-panel">
                    <h3 className="font-display text-lg mb-3">Resolver</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <Label>Motivo (obligatorio)</Label>
                            <Textarea
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                placeholder="Ej: Verificado contra ID físico. RFC confirmado en SAT."
                                data-testid="motivo-resolucion"
                                rows={3}
                            />
                        </div>
                        <div className="flex flex-col justify-center gap-3">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={aplicarPersona}
                                    onChange={(e) => setAplicarPersona(e.target.checked)}
                                    data-testid="aplicar-persona-check"
                                />
                                Aplicar a persona (crea o actualiza)
                            </label>
                            <div className="text-[11px] text-muted-foreground">
                                Si aprobás con correcciones, los valores editados sustituyen al LLM.
                                Se registra en bitácora con motivo y hash del prompt.
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4">
                        <Button
                            onClick={() => resolver("aprobado_extraccion")}
                            disabled={busy || !motivo.trim()}
                            data-testid="hil-resolver-btn"
                        >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Aprobar como está
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => resolver("corregido_y_aprobado")}
                            disabled={busy || !motivo.trim()}
                            data-testid="hil-corregir-btn"
                        >
                            <Save className="h-4 w-4 mr-1" /> Corregir y aprobar
                        </Button>
                        <Dialog open={openReject} onOpenChange={setOpenReject}>
                            <DialogTrigger asChild>
                                <Button variant="destructive" disabled={busy} data-testid="hil-rechazar-btn">
                                    <XCircle className="h-4 w-4 mr-1" /> Rechazar documento
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Rechazar documento</DialogTitle></DialogHeader>
                                <div className="text-sm text-muted-foreground">
                                    Confirmar rechazo. El documento queda marcado como no válido para KYC.
                                    Requiere motivo antes de confirmar.
                                </div>
                                <DialogFooter>
                                    <Button
                                        variant="destructive"
                                        onClick={() => { resolver("rechazado_documento"); setOpenReject(false); }}
                                        disabled={busy || !motivo.trim()}
                                        data-testid="hil-rechazar-confirm"
                                    >
                                        Confirmar rechazo
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            )}

            {isResuelto && (
                <div className="mt-6 rounded-md border border-[color:var(--brand-exito)]/40 bg-[color:var(--brand-exito)]/5 p-4 text-sm" data-testid="resuelto-panel">
                    <div className="flex items-center gap-2 font-medium text-[color:var(--brand-exito)]">
                        <CheckCircle2 className="h-4 w-4" /> Resuelto por {item.resuelto_por} el {item.resuelto_at?.slice(0, 16)}
                    </div>
                    <div className="text-muted-foreground mt-1">
                        Resolución: <strong>{item.resolucion?.replace(/_/g, " ")}</strong> · {item.motivo_resolucion}
                    </div>
                </div>
            )}
        </div>
    );
}
